// SubPromptsList — /admin/sub-prompts. Sortable table of every
// registered sub-prompt: id / stage / layer / prompt file / 24h
// invocations / median duration / success rate (§7.8.7).
//
// Backend endpoint: GET /api/v1/admin/sub-prompts. Not yet
// deployed; if it returns 404/501 we render BackendPendingBanner.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import {
  EndpointUnavailableError,
  listSubPrompts,
  type SubPromptSummary,
} from "../api/traceApi";
import { BackendPendingBanner } from "../components/BackendPendingBanner";
import { Table, THead, TBody, TR, TH, TD } from "../components/Table";

type SortKey =
  | "id"
  | "stage"
  | "layer"
  | "recent_invocations_24h"
  | "median_duration_ms"
  | "success_rate"
  | "cancel_rate";

function compareBy(k: SortKey, dir: 1 | -1) {
  return (a: SubPromptSummary, b: SubPromptSummary) => {
    const av = a[k];
    const bv = b[k];
    if (av === bv) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    return av < bv ? -1 * dir : 1 * dir;
  };
}

function formatPct(v: number | undefined): string {
  if (v === undefined) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function formatMs(v: number | undefined): string {
  if (v === undefined) return "—";
  return `${v}ms`;
}

export function SubPromptsList() {
  const { data: me } = useUser();
  const [sortKey, setSortKey] = useState<SortKey>("id");
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  const q = useQuery({
    queryKey: ["admin", "sub-prompts"],
    queryFn: listSubPrompts,
    retry: (n, err) => !(err instanceof EndpointUnavailableError) && n < 2,
  });

  const sorted = useMemo(() => {
    const items = q.data ?? [];
    return [...items].sort(compareBy(sortKey, sortDir));
  }, [q.data, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };

  if (!me) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
          Sub-prompts
        </h1>
        <p className="font-mono text-[11px] text-ink-400 mb-6">
          Registered sub-prompts across all stages (V2)
        </p>
        {q.error instanceof EndpointUnavailableError && (
          <BackendPendingBanner
            feature="Sub-prompt registry"
            endpoint="GET /api/v1/admin/sub-prompts"
          />
        )}
        {q.error && !(q.error instanceof EndpointUnavailableError) && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}
        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.data && (
          <Table>
            <THead>
              <SortableTH k="id" current={sortKey} dir={sortDir} onSort={onSort}>
                id
              </SortableTH>
              <SortableTH k="stage" current={sortKey} dir={sortDir} onSort={onSort}>
                stage
              </SortableTH>
              <SortableTH k="layer" current={sortKey} dir={sortDir} onSort={onSort}>
                layer
              </SortableTH>
              <TH>prompt file</TH>
              <SortableTH k="recent_invocations_24h" current={sortKey} dir={sortDir} onSort={onSort}>
                24h
              </SortableTH>
              <SortableTH k="median_duration_ms" current={sortKey} dir={sortDir} onSort={onSort}>
                median ms
              </SortableTH>
              <SortableTH k="success_rate" current={sortKey} dir={sortDir} onSort={onSort}>
                success
              </SortableTH>
              <SortableTH k="cancel_rate" current={sortKey} dir={sortDir} onSort={onSort}>
                cancel
              </SortableTH>
            </THead>
            <TBody>
              {sorted.map((s) => (
                <TR key={s.id ?? ""}>
                  <TD>
                    <Link
                      to={`/admin/sub-prompts/${encodeURIComponent(s.id ?? "")}`}
                      className="font-mono text-[11px] text-ink-100 hover:text-accent"
                    >
                      {s.id ?? "—"}
                    </Link>
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {s.stage ?? "—"}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {s.layer ?? "—"}
                  </TD>
                  <TD className="font-mono text-[10px] text-ink-400">
                    {s.prompt_file ?? "—"}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {s.recent_invocations_24h ?? 0}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {formatMs(s.median_duration_ms)}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {formatPct(s.success_rate)}
                  </TD>
                  <TD
                    className={`font-mono text-[11px] ${
                      (s.cancel_rate ?? 0) > 0 ? "text-amber-300" : "text-ink-500"
                    }`}
                  >
                    <span
                      title="Fraction of invocations cancelled by an errgroup sibling's earlier failure (cascade victims). 0% means failures here are genuine; high % means this sub-prompt is a downstream victim of someone else."
                    >
                      {formatPct(s.cancel_rate)}
                    </span>
                  </TD>
                </TR>
              ))}
              {sorted.length === 0 && !q.isLoading && !q.error && (
                <TR>
                  <TD>
                    <span className="text-ink-400">No sub-prompts registered.</span>
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        )}
      </main>
    </div>
  );
}

function SortableTH({
  k,
  current,
  dir,
  onSort,
  children,
}: {
  k: SortKey;
  current: SortKey;
  dir: 1 | -1;
  onSort: (k: SortKey) => void;
  children: React.ReactNode;
}) {
  const isActive = k === current;
  return (
    <th className="text-left font-mono text-[10px] uppercase tracking-widest text-ink-400 py-2 px-3">
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`hover:text-ink-100 ${isActive ? "text-ink-100" : ""}`}
      >
        {children}
        {isActive && (dir === 1 ? " ↑" : " ↓")}
      </button>
    </th>
  );
}
